import { API_URLS } from '@chessx/shared/api-routes'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import axiosInstance from '../network/axiosInstance'

import { toastMixin, getPWNBalance } from '../utils/interact'
import type { ApiError, User } from '../types/api'

export interface ArmyHeroItem {
  _id: string
  inUse?: boolean
  hero?: { color: string; pieceType: string }
  [k: string]: unknown
}

interface UserState {
  user: User | null
  pwns: string
  userId: string | null
  army: ArmyHeroItem[] | null
  walletAddress: string | null
  guestId: string | null
  token: string | null
  refreshToken: string | null
  strength: number | null
  level: number
  completed: number
  placedHeroes: Record<string, ArmyHeroItem> | null
  armySize: number

  setUser: (value: User | null) => void
  setUserId: (value: string | null) => void
  setPwnBalance: (value: string) => void
  setArmy: (value: ArmyHeroItem[] | null) => void
  setPlacedHeroes: (value: Record<string, ArmyHeroItem> | null) => void
  setWalletAddress: (value: string | null) => void
  setGuestId: (value: string | null) => void
  setToken: (value: string | null) => void
  setRefreshToken: (value: string | null) => void

  fetchHistory: (page: number, count: number) => Promise<unknown>
  fetchUser: (id?: string | null) => Promise<unknown>
  fetchProgress: () => Promise<void>
  updateUser: (params: Record<string, unknown>) => Promise<void>
  fetchPWNBalance: (address: string | null | undefined) => Promise<void>
  fetchArmy: (color?: string) => Promise<void>
  placeHero: (armyHero: ArmyHeroItem, color?: string) => Promise<void>
  removeHero: (armyHero: ArmyHeroItem, color?: string) => Promise<void>
}

const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      pwns: 'X',
      userId: null,
      army: null,
      walletAddress: null,
      guestId: null,
      token: null,
      refreshToken: null,
      strength: null,
      level: 1,
      completed: 0,
      placedHeroes: null,
      armySize: 0,

      setUser: value => {
        set({ user: value })
      },
      setUserId: value => {
        set({ userId: value })
      },
      setPwnBalance: value => {
        set({ pwns: value })
      },
      setArmy: value => {
        set({ army: value })
      },
      setPlacedHeroes: value => {
        set({ placedHeroes: value })
      },
      setWalletAddress: value => {
        set({ walletAddress: value })
      },
      setGuestId: value => {
        set({ guestId: value })
      },
      setToken: value => {
        set({ token: value })
      },
      setRefreshToken: value => {
        set({ refreshToken: value })
      },

      fetchHistory: async (page, count) => {
        try {
          const historyUrl = API_URLS.ADMIN_GAMES_HISTORY
          const params = { userId: get().userId, page, count }
          const res = await axiosInstance.post(historyUrl, params)

          if (res.status === 200) {
            return res?.data
          }

          return null
        } catch (e) {
          const err = e as ApiError
          toastMixin.fire({ title: `ERROR fetching games history: ${err.message}`, icon: 'error' })
        }
      },

      fetchUser: async id => {
        try {
          if (!id) {
            return get().user
          }

          const userUrl = API_URLS.GET_USER_DETAIL
          const res = await axiosInstance.get(userUrl)

          if (res?.status === 200) {
            set({ user: res.data, userId: res.data._id })
          }
        } catch (e) {
          const err = e as ApiError
          toastMixin.fire({ title: `ERROR fetching user: ${err.message}`, icon: 'error' })
        }
      },

      fetchProgress: async () => {
        try {
          if (!get().token) return
          const progressUrl = API_URLS.ADMIN_USER_PROGRESS
          const res = await axiosInstance.get(progressUrl)
          if (res?.status === 200) {
            set({
              strength: res?.data?.strength || 1200,
              level: res?.data?.level,
              completed: res?.data?.completed
            })
          }
        } catch (e) {
          const err = e as ApiError
          console.error('error', err)
          toastMixin.fire({ title: `ERROR fetching user progress: ${err.message}`, icon: 'error' })
        }
      },

      updateUser: async params => {
        const url = API_URLS.UPDATE_USER
        const result = await axiosInstance.put(url, params)
        if (result?.status === 200) {
          set({ user: result?.data })
        }
      },

      fetchPWNBalance: async address => {
        try {
          const pwns = await getPWNBalance(address)
          set({ pwns })
        } catch (e) {
          const err = e as ApiError
          toastMixin.fire({ title: `Error fetching PWN balance: ${err.message}`, icon: 'error' })
        }
      },

      fetchArmy: async color => {
        try {
          const params = {
            color: color || 'w,b',
            pieceType: 'p,n,b,r,q,k'
          }
          const url = API_URLS.GET_ARMY
          const res = await axiosInstance.get(url, { params })
          if (res?.status === 200) {
            const placedHeroes: Record<string, ArmyHeroItem> = {}
            const army: ArmyHeroItem[] = []
            if (res.data?.data?.length) {
              res.data.data.forEach((ele: ArmyHeroItem) => {
                if (ele.inUse) {
                  if (ele?.hero?.color !== undefined && ele?.hero?.pieceType !== undefined) {
                    placedHeroes[ele.hero.color + ele.hero.pieceType] = ele
                  }
                } else {
                  army.push(ele)
                }
              })
            }
            set({ army, placedHeroes, armySize: res.data.totalCount })
          }
        } catch (e) {
          const err = e as ApiError
          console.error('error', err)
          set({ army: null, placedHeroes: null, armySize: 0 })
          toastMixin.fire({
            title: `Error fetching the army: ${err?.response?.data?.error}`,
            icon: 'error'
          })
        }
      },
      placeHero: async (armyHero, color) => {
        try {
          const url = API_URLS.ADMIN_USE_HERO
          const params = {
            _id: armyHero._id
          }
          const res = await axiosInstance.put(url, params)
          if (res?.status === 200) {
            get().fetchArmy(color)
          }
        } catch (err) {
          const e = err as ApiError
          toastMixin.fire({ title: `ERROR placing Hero: ${e.message}`, icon: 'error' })
          throw new Error(e.message ?? 'Error placing Hero')
        }
      },
      removeHero: async (armyHero, color) => {
        try {
          const url = API_URLS.REMOVE_HERO
          const params = {
            _id: armyHero._id
          }
          const res = await axiosInstance.put(url, params)
          if (res?.status === 200) {
            get().fetchArmy(color)
          }
        } catch (err) {
          const e = err as ApiError
          toastMixin.fire({ title: `ERROR removing Hero: ${e.message}`, icon: 'error' })
          throw new Error(e.message ?? 'Error removing Hero')
        }
      }
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => localStorage)
    }
  )
)

export default useUserStore
